/**
 * A paleta Ctrl+K, portada de knowledge.js:704-771.
 *
 * Ela indexa o que o painel já carregou — arquivo, rota, decisão, nota, sessão,
 * proposta pendente e as abas — e não faz requisição nova. É o que a torna
 * instantânea, e também o que define o limite dela: para procurar dentro do
 * texto da doc existe a busca do topo, e o estado vazio diz isso.
 *
 * Acessibilidade que vem de lá e continua: o foco fica no campo, a opção sob o
 * cursor é anunciada por `aria-activedescendant` (em vez de mover o foco e
 * matar a digitação), e o Tab circula dentro do diálogo.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { chaves, type RespostaNotas } from '../api/consultas';
import type { DecisaoCompleta, Documento, Proposta, Sessao } from '../api/tipos';
import type { GrafoBruto } from '../grafo/nucleo';
import { ABAS } from '../layout/catalogo';
import { ESPECIE_DE_PROPOSTA, rotulo, SITUACAO_DA_SESSAO } from '../dados/rotulos';

interface Item {
  t: string;
  rotulo: string;
  sub: string;
  ir: () => void;
}

const LIMITE = 60;

// As abas que a paleta oferece, na ordem de knowledge.js:724.
//
// Fora da lista ficam as duas do admin e a Busca: quem quer buscar texto tem o
// campo do topo com o atalho `/`, e é isso que o estado vazio da paleta diz.
const ABAS_DA_PALETA = [
  'overview', 'doc', 'graph', 'sessions', 'review', 'decisions', 'impact', 'health', 'notes', 'commits',
];

export function Paleta({ slug, aberta, aoFechar, aoAbrirTour }: {
  slug: string;
  aberta: boolean;
  aoFechar: () => void;
  aoAbrirTour: () => void;
}) {
  // fechada, a paleta não existe: os dados dela saem do cache no momento em que
  // ela abre, como o `buildPalette()` de knowledge.js:713 fazia no `palOpen`
  if (!aberta) return null;
  return <Aberta slug={slug} aoFechar={aoFechar} aoAbrirTour={aoAbrirTour} />;
}

function Aberta({ slug, aoFechar, aoAbrirTour }: {
  slug: string;
  aoFechar: () => void;
  aoAbrirTour: () => void;
}) {
  const navegar = useNavigate();
  const fila = useQueryClient();
  const campo = useRef<HTMLInputElement>(null);
  const caixa = useRef<HTMLDivElement>(null);
  const anterior = useRef<Element | null>(null);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);

  const itens = useMemo<Item[]>(() => {
    // leitura de cache, sem disparar requisição: a paleta indexa o que o painel
    // já tem. A casca e a barra lateral carregam docs, notas, decisões,
    // propostas e sessões em qualquer aba; o grafo entra na lista só depois de
    // alguém abrir a aba dele, que é o mesmo que o painel atual fazia.
    const docs = fila.getQueryData<Documento[]>(chaves.docs(slug));
    const grafo = fila.getQueryData<GrafoBruto>(chaves.grafo(slug));
    const decisoes = fila.getQueryData<{ decisions: DecisaoCompleta[] }>(chaves.decisoes(slug));
    const propostas = fila.getQueryData<{ proposals: Proposta[] }>(chaves.propostas(slug));
    const notas = fila.getQueryData<RespostaNotas>(chaves.notas(slug, null));
    const sessoes = fila.getQueryData<{ sessions: Sessao[] }>(chaves.sessoes(slug));

    const irPara = (aba: string, busca?: string) => () => navegar(`/p/${slug}/${aba}${busca || ''}`);
    const abrirDoc = (k: string) => irPara('doc', `?key=${encodeURIComponent(k)}`);
    const lista: Item[] = [];

    for (const d of docs || []) {
      lista.push({
        t: d.unit_kind === 'file' ? 'file' : 'ir',
        rotulo: d.unit_key,
        sub: d.unit_kind,
        ir: abrirDoc(d.unit_key),
      });
    }
    for (const n of grafo?.nodes || []) {
      if (n.kind !== 'endpoint') continue;
      lista.push({
        t: 'rota',
        rotulo: n.node_key,
        sub: n.file || '',
        ir: irPara('graph', `?no=${encodeURIComponent(n.node_key)}`),
      });
    }
    for (const d of decisoes?.decisions || []) {
      lista.push({ t: 'decisão', rotulo: d.title, sub: d.unit_key, ir: abrirDoc(d.unit_key) });
    }
    for (const p of (propostas?.proposals || []).filter((x) => x.status === 'pending')) {
      lista.push({
        t: 'proposta',
        rotulo: `${p.unit_key} — ${rotulo(ESPECIE_DE_PROPOSTA, p.kind)}`,
        sub: p.origin || '',
        ir: irPara('review'),
      });
    }
    for (const [k, n] of Object.entries(notas?.counts || {})) {
      if (!n) continue;
      lista.push({ t: 'nota', rotulo: `${k} · ${n} nota(s)`, sub: 'notas do time', ir: abrirDoc(k) });
    }
    for (const s of sessoes?.sessions || []) {
      lista.push({
        t: 'sessão',
        rotulo: s.task,
        sub: `${s.author_email || ''} · ${rotulo(SITUACAO_DA_SESSAO, s.status)}`,
        ir: irPara('sessions', `?id=${encodeURIComponent(s.id)}`),
      });
    }
    for (const id of ABAS_DA_PALETA) {
      const aba = ABAS.find((a) => a.id === id);
      if (!aba) continue;
      lista.push({ t: 'ir', rotulo: `Abrir ${aba.rotulo}`, sub: 'aba', ir: irPara(aba.id) });
    }
    lista.push({
      t: 'ir',
      rotulo: 'Tutorial animado',
      sub: '/tutorial',
      ir: () => window.open('/tutorial', '_blank'),
    });
    lista.push({ t: 'ir', rotulo: 'Tour guiado do painel', sub: 'onboarding', ir: aoAbrirTour });
    return lista;
  }, [slug, navegar, aoAbrirTour, fila]);

  const achados = useMemo(() => {
    const s = q.toLowerCase().trim();
    const casa = s
      ? itens.filter((i) => `${i.rotulo} ${i.sub} ${i.t}`.toLowerCase().includes(s))
      : itens;
    return casa.slice(0, LIMITE);
  }, [itens, q]);

  useEffect(() => setSel(0), [q]);

  // o foco volta para quem abriu a paleta ao fechar, como em app.js:745-757
  useEffect(() => {
    anterior.current = document.activeElement;
    campo.current?.focus();
    const volta = anterior.current;
    return () => {
      if (volta instanceof HTMLElement) volta.focus();
    };
  }, []);

  useEffect(() => {
    function noTeclado(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); aoFechar(); return; }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!achados.length) return;
        const d = e.key === 'ArrowDown' ? 1 : -1;
        setSel((i) => (i + d + achados.length) % achados.length);
        return;
      }
      if (e.key === 'Enter') {
        const escolhido = achados[sel];
        if (!escolhido) return;
        e.preventDefault();
        aoFechar();
        escolhido.ir();
        return;
      }
      if (e.key !== 'Tab') return;
      // foco preso: Tab circula dentro do diálogo em vez de vazar para a página
      const foco = [...(caixa.current?.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])',
      ) || [])];
      if (!foco.length) return;
      const primeiro = foco[0];
      const ultimo = foco[foco.length - 1];
      if (e.shiftKey && document.activeElement === primeiro) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primeiro.focus(); }
    }
    document.addEventListener('keydown', noTeclado, true);
    return () => document.removeEventListener('keydown', noTeclado, true);
  }, [achados, sel, aoFechar]);

  const idDaOpcao = (i: number) => `pal-op-${i}`;

  return (
    <div
      id="pal"
      className="pal show"
      role="dialog"
      aria-modal="true"
      aria-label="paleta de comandos"
      onClick={(e) => { if (e.target === e.currentTarget) aoFechar(); }}
    >
      <div className="pal-box" ref={caixa}>
        <input
          id="pal-q"
          ref={campo}
          placeholder="arquivo, rota, decisão, nota, proposta, comando…"
          autoComplete="off"
          role="combobox"
          aria-expanded
          aria-controls="pal-list"
          aria-autocomplete="list"
          aria-label="buscar no painel"
          aria-activedescendant={achados.length ? idDaOpcao(sel) : undefined}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="pal-list" id="pal-list" role="listbox" aria-label="resultados">
          {achados.length
            ? achados.map((i, ix) => (
              <div
                key={`${i.t}-${i.rotulo}-${ix}`}
                id={idDaOpcao(ix)}
                className={`pal-item${ix === sel ? ' on' : ''}`}
                role="option"
                aria-selected={ix === sel}
                onMouseMove={() => setSel(ix)}
                onClick={() => { aoFechar(); i.ir(); }}
              >
                <span className={`t ${i.t}`}>{i.t}</span>
                <span className="lbl">{i.rotulo}</span>
                <span className="sub">{i.sub}</span>
              </div>
            ))
            : (
              <div className="pal-item">
                <span className="lbl muted">
                  Nada com esse nome. A paleta procura em arquivos, rotas, decisões, notas, sessões e
                  abas — para buscar dentro do texto da doc, use a busca do topo (/).
                </span>
              </div>
            )}
        </div>
        <div className="pal-foot">
          <span>
            <span className="kbd">↑</span><span className="kbd">↓</span> navegar ·{' '}
            <span className="kbd">Enter</span> abrir · <span className="kbd">Esc</span> fechar
          </span>
          <span id="pal-hint" role="status" aria-live="polite">{achados.length} resultado(s)</span>
        </div>
      </div>
    </div>
  );
}
